<template>
  <div id="pageItemList">
    <page-header :title="$t('basicReport')"></page-header>
    <v-container grid-list-xl fluid>
      <v-layout row wrap>
        <v-flex lg12>
          <v-card>
            <v-card-text>
              <v-form ref="editCreateForm">
                <v-container grid-list-md fluid>
                  <v-layout wrap justify-space-between>
                    <v-flex xs8>
                      <v-autocomplete
                        name="vehicles"
                        :label="$t('vehicle')"
                        :loading="vehicleIsLoading"
                        :items="vehicles"
                        :search-input.sync="vehicleSearch"
                        v-model="reportParams.vehicles"
                        cache-items
                        clearable
                        prepend-icon="train"
                        item-text="name"
                        item-value="id"
                        v-validate="'required'"
                        :data-vv-as="$t('vehicle')"
                        :error-messages="errors.collect('vehicles')"
                        multiple
                      >
                        <template v-slot:prepend-item>
                          <v-list-tile ripple @click="toggleSelectAllVehicles">
                            <v-list-tile-action>
                              <v-icon
                                :color="
                                  reportParams.vehicles.length > 0
                                    ? 'indigo darken-4'
                                    : ''
                                "
                                >{{ allVehiclesIcon }}</v-icon
                              >
                            </v-list-tile-action>
                            <v-list-tile-content>
                              <v-list-tile-title>{{
                                $t("selectAllVehicles")
                              }}</v-list-tile-title>
                            </v-list-tile-content>
                          </v-list-tile>
                          <v-divider class="mt-2"></v-divider>
                        </template>
                      </v-autocomplete>
                    </v-flex>
                    <v-flex xs2>
                      <v-menu
                        :close-on-content-click="false"
                        v-model="date.fromMenu"
                        lazy
                        transition="scale-transition"
                        offset-y
                        full-width
                        min-width="290px"
                      >
                        <v-text-field
                          name="date_from"
                          slot="activator"
                          :value="
                            reportParams.dateFrom
                              ? $moment(reportParams.dateFrom).format('LL')
                              : null
                          "
                          :label="$t('dateFrom')"
                          v-validate="'required'"
                          :data-vv-as="$t('dateFrom')"
                          :error-messages="errors.collect('date_from')"
                          readonly
                        ></v-text-field>
                        <v-date-picker
                          v-model="reportParams.dateFrom"
                          @input="date.fromMenu = false"
                          :locale="LANG"
                          reactive
                          :max="$moment().format('YYYY-MM-DD')"
                        ></v-date-picker>
                      </v-menu>
                    </v-flex>
                    <v-flex xs2>
                      <v-menu
                        :close-on-content-click="false"
                        v-model="date.untilMenu"
                        lazy
                        transition="scale-transition"
                        offset-y
                        full-width
                        min-width="290px"
                      >
                        <v-text-field
                          name="date_until"
                          slot="activator"
                          :value="
                            reportParams.dateUntil
                              ? $moment(reportParams.dateUntil).format('LL')
                              : null
                          "
                          :label="$t('dateUntil')"
                          v-validate="'required'"
                          :data-vv-as="$t('dateUntil')"
                          :error-messages="errors.collect('date_until')"
                          readonly
                        ></v-text-field>
                        <v-date-picker
                          v-model="reportParams.dateUntil"
                          @input="date.untilMenu = false"
                          :locale="LANG"
                          reactive
                          :max="$moment().format('YYYY-MM-DD')"
                        ></v-date-picker>
                      </v-menu>
                    </v-flex>
                  </v-layout>
                </v-container>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                :href="getDownloadUrl"
                :disabled="!formIsReady"
                target="_blank"
                color="success"
              >
                {{ $t("download") }}
                <v-icon right>save_alt</v-icon>
              </v-btn>
              <v-btn @click="generate()" color="blue-grey" class="white--text">
                {{ $t("generate") }}
                <v-icon right>receipt</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-flex>
        <v-flex lg12>
          <v-card>
            <v-divider></v-divider>
            <v-card-text class="pa-0">
              <v-data-table
                :headers="headers"
                :items="report"
                :loading="reportIsLoading"
                class="elevation-1"
                hide-actions
              >
                <template slot="items" slot-scope="props">
                  <td>
                    <au-vehicle :vehicle="props.item.vehicle" />
                  </td>
                  <td>
                    {{ props.item.date_time_from | moment("LLL") | lackOfData }}
                  </td>
                  <td>
                    {{
                      props.item.date_time_until | moment("LLL") | lackOfData
                    }}
                  </td>
                  <td>{{ props.item.distance }} km</td>
                  <td>{{ props.item.issue_count }}</td>
                  <td>{{ props.item.critical_issue_count }}</td>
                  <td>{{ props.item.vehicle_inaccessible_in_hours }}</td>
                  <td>
                    {{ props.item.vehicle_readiness_coefficient | percent }}
                  </td>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import _ from "lodash";
import { FETCH_BASIC_REPORT, FETCH_VEHICLES } from "@/store/actions.type";
import JwtService from "../../common/jwt.service";
import store from "@/store";
import { RESET_REPORT } from "../../store/actions.type";

export default {
  data() {
    return {
      date: {
        fromMenu: false,
        untilMenu: false
      },
      vehicleSearch: "",
      reportParams: {
        vehicles: [],
        dateFrom: null,
        dateUntil: null
      },
      LANG: process.env.VUE_APP_LANG,
      headers: [
        { text: this.$t("vehicle"), sortable: false },
        { text: this.$t("dataFrom"), sortable: false },
        { text: this.$t("dataUntil"), sortable: false },
        { text: this.$t("distance"), sortable: false },
        { text: this.$t("issuesCount"), sortable: false },
        { text: this.$t("criticalIssuesCount"), sortable: false },
        { text: "Dostępność - Godziny", sortable: false },
        { text: this.$t("vehicleReadiness"), sortable: false }
      ]
    };
  },
  computed: {
    ...mapGetters([
      "report",
      "reportIsLoading",
      "vehicles",
      "vehicleIsLoading"
    ]),
    getDownloadUrl() {
      return (
        process.env.VUE_APP_API_URL +
        "/report/basic/file?dateFrom=" +
        this.reportParams.dateFrom +
        "&dateUntil=" +
        this.reportParams.dateUntil +
        "&vehicles[]=" +
        this.reportParams.vehicles.join("&vehicles[]=") +
        "&bearer=" +
        JwtService.getToken()
      );
    },
    formIsReady() {
      return (
        !!this.reportParams.vehicles.length &&
        this.reportParams.dateFrom &&
        this.reportParams.dateUntil
      );
    },
    selectedAllVehicles() {
      return this.reportParams.vehicles.length === this.vehicles.length;
    },
    likesSomeVehicles() {
      return this.reportParams.vehicles.length > 0 && !this.selectedAllVehicles;
    },
    allVehiclesIcon() {
      if (this.selectedAllVehicles) return "check_box";
      if (this.likesSomeVehicles) return "indeterminate_check_box";
      return "check_box_outline_blank";
    }
  },
  watch: {
    vehicleSearch: _.debounce(function(val) {
      this.$store.dispatch(FETCH_VEHICLES, [{ rowsPerPage: -1 }, val]);
    }, 300)
  },
  methods: {
    generate() {
      this.$validator.validateAll().then(result => {
        if (result) {
          this.$store.dispatch(FETCH_BASIC_REPORT, this.reportParams);
        }
      });
    },
    toggleSelectAllVehicles() {
      this.$nextTick(() => {
        if (this.selectedAllVehicles) {
          this.reportParams.vehicles = [];
        } else {
          this.reportParams.vehicles = this.vehicles.map(vehicle => {
            return vehicle.id;
          });
        }
      });
    }
  },
  beforeRouteEnter(to, from, next) {
    Promise.all([store.dispatch(RESET_REPORT)]).then(() => {
      next();
    });
  }
};
</script>

<style scoped></style>
